<template>
  <div class="timeline" style="min-width: 224px">
    <div class="unit" :class="getTimelineClass(!order.events || !order.events.length ? 0 : -1)">
      <div class="time" v-html="generateDateTimeHtml(order.saleTime)">
      </div>
      <div class="state">
        <div class="state-icon">
          <span class="material-icons md-18">
            list_alt
          </span>
        </div>
      </div>
      <div class="info">
        <h6>{{ $t('order.timeline.create') }}</h6>
        <p>{{ order.paymentTypeName }}</p>
        <p>{{ order.invoiceTypeName }}</p>
      </div>
    </div>

    <template  v-for="(event, index) in order.events">
      <div class="unit" v-if="canEventShow(event)" :key="event.status" :class="getTimelineClass(index)">
        <div class="time" v-html="generateDateTimeHtml(event.time)">
        </div>
        <div class="state">
          <div class="state-icon">
            <span class="material-icons md-18">
              {{ getIcon(event.status) }}
            </span>
          </div>
        </div>
        <div class="info" v-html="generateContentHtml(event.category, event.status)" />
      </div>
    </template>

    <div class="unit futuristic"  v-if="!hasReceivedEvent && !hasCancelledEvent" :class="getTimelineClass(-1)">
      <div class="time">
      </div>
      <div class="state">
        <div class="state-icon">
          <span class="material-icons md-18">
            {{ isHomeDelivery ? getIcon('received') : getIcon('storeReceived') }}
          </span>
        </div>
      </div>
      <div class="info" v-html="isHomeDelivery ? generateContentHtml('Delivery', 'received') : generateContentHtml('Delivery', 'storeReceived')" />
    </div>
  </div>
</template>

<script>
/* eslint-disable max-classes-per-file */
import dateTimeFormatter from '@/utils/dateTimeFormatter';

const cargoTrackingUrl = {
  sevenEleven: 'https://eservice.7-11.com.tw/E-Tracking/search.aspx',
  hct: 'https://www.hct.com.tw/Search/SearchGoods_n.aspx',
  tCat: 'https://www.t-cat.com.tw/Inquire/Trace.aspx',
};

export default {
  props: {
    order: {
      type: Object,
    },
    currentOnly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // 是否為宅配
    isHomeDelivery() {
      const vm = this;
      /*
       * 1. NopCommerce 訂單 logisticsProviderId 字串包含 Delivery
       * 2. 舊 Telligent 訂單 deliveryMethodId 為 1 (0: NopCommerce, 1：宅配, 2：門市取貨)
       */
      return (vm.order.logisticsProviderId && /Delivery/i.test(vm.order.logisticsProviderId)) || vm.order.deliveryMethodId === 1;
    },
    // 是否有配達事件
    hasReceivedEvent() {
      const vm = this;
      if (vm.isHomeDelivery) return vm.order.events.find((x) => x.status === 'received');
      return vm.order.events.find((x) => x.status === 'storeReceived' || x.status === 'received');
    },
    // 是否有取消事件
    hasCancelledEvent() {
      const vm = this;
      return vm.order.events.find((x) => x.status === 'cancelled');
    },
    // 取得最新事件的index
    currentIndex() {
      const vm = this;
      let currentIndex = 0;
      vm.order.events.forEach((event, index) => {
        if (vm.canEventShow(event)) {
          currentIndex = index;
        }
      });
      return currentIndex;
    },
  },
  methods: {
    // 取得時間線class
    getTimelineClass(index) {
      const vm = this;
      const isCurrent = index === vm.currentIndex;

      return {
        current: isCurrent,
        'd-none': !isCurrent && vm.currentOnly,
        'd-inherit': isCurrent && vm.currentOnly,
      };
    },
    // 生成 Date Time Html
    generateDateTimeHtml(time) {
      const dateTime = dateTimeFormatter.toDateTime(time);
      const stringBuilder = [];
      stringBuilder.push(
        dateTime.getFullYear(),
        dateTimeFormatter.padZero(dateTime.getMonth() + 1, 2),
        dateTimeFormatter.padZero(dateTime.getDate(), 2),
      );

      return `<time>${dateTimeFormatter.padZero(dateTime.getHours(), 2)}:${dateTimeFormatter.padZero(dateTime.getMinutes(), 2)}</time>
        <p>${stringBuilder.join('/')}</p>`;
    },
    // 取得 Icon
    getIcon(status) {
      switch (status) {
        case 'processing':
        case '21':
        case '71':
        case '72':
          return 'check';
        case 'delivered':
          return 'local_shipping';
        case 'storeReceived':
          return 'storefront';
        case 'received':
          if (this.isHomeDelivery) return 'storefront';
          return 'check';
        case 'retrieve':
          return 'local_shipping';
        default:
          return '';
      }
    },
    // 生成內容 Html
    generateContentHtml(category, status) {
      const vm = this;
      switch (status) {
        case 'processing':
          return `<h6>${vm.$t('order.timeline.undelivered')}</h6>`;
        case 'delivered':
        {
          let content = `<h6>${vm.$t('order.timeline.delivered')}</h6>
            <p>${vm.order.shippingMethodName || ''}</p>
            <p>${vm.order.logisticsNo || ''}</p>`;

          switch (vm.order.logisticsProviderId) {
            case 'ECPay_ConvenienceStore_7ELEVEN':
              content = content.concat(`<a href="${cargoTrackingUrl.sevenEleven}" target="_blank"><u>${vm.$t('order.cargo-tracking')}</u></a>`);
              break;
            case 'HCT_Delivery':
              content = content.concat(`<a href="${cargoTrackingUrl.hct}" target="_blank"><u>${vm.$t('order.cargo-tracking')}</u></a>`);
              break;
            case 'ECPay_Delivery_TCAT':
              content = content.concat(`<a href="${cargoTrackingUrl.tCat}" target="_blank"><u>${vm.$t('order.cargo-tracking')}</u></a>`);
              break;
            default:
              break;
          }

          return content;
        }
        case 'storeReceived':
          return `<h6>${vm.$t('order.timeline.store-received')}</h6>
            <p>${vm.order.memberName || ''}</p>
            <p>${vm.order.memberMobile || ''}</p>
            <p>${vm.order.deptName || ''}</p>
            <p>${vm.order.address || ''}</p>`;
        case 'received':
          if (vm.isHomeDelivery) {
            return `<h6>${vm.$t('order.timeline.received')}</h6>
              <p>${vm.order.memberName || ''}</p>
              <p>${vm.order.memberMobile || ''}</p>
              <p>${vm.order.city || ''}${vm.order.district || ''}${vm.order.address || ''}</p>`;
          }
          return `<h6>${vm.$t('order.timeline.completed')}</h6>`;
        case 'retrieve':
          return `<h6>${vm.$t('order.timeline.retrieve')}</h6>`;
        case '21':
          switch (category) {
            case 'Cancel':
              return `<h6>${vm.$t('order.timeline.cancel.processing')}</h6>`;
            case 'Return':
              return `<h6>${vm.$t('order.timeline.return.processing')}</h6>`;
            default:
              return '';
          }
        case '71':
          return `<h6>${vm.$t('order.timeline.cancel.complete')}</h6>`;
        case '72':
        {
          let firstHalf;
          let secondHalf;

          switch (category) {
            case 'Cancel':
              firstHalf = `<h6>${vm.$t('order.timeline.cancel.complete')}</h6>`;
              break;
            case 'Return':
              firstHalf = `<h6>${vm.$t('order.timeline.return.complete')}</h6>`;
              break;
            default:
              firstHalf = '';
              break;
          }

          if (vm.order.paymentType.includes('COD')) {
            secondHalf = `<p>${vm.$t('order.atmreturn')}</p>`;
          } else if (vm.order.paymentType.includes('Credit')) {
            secondHalf = `<p>${vm.$t('order.creditcardreturn')}</p>`;
          } else {
            secondHalf = '';
          }

          return firstHalf + secondHalf;
        }
        default:
          return '';
      }
    },
    // 檢查event是否要顯示
    canEventShow(event) {
      return (event.category === 'Delivery' && event.status !== 'cancelled')
             || (event.category === 'Return' && (event.status === '21' || event.status === '72'))
             || (event.category === 'Cancel' && (event.status === '21' || event.status === '72' || event.status === '71'));
    },
  },
};
</script>

<style scoped>
.d-inherit {
  display: inherit !important;
}
</style>
